// src/lib/stockMovements.js
//
// #349, Session 79.22b.6.1g -- ONE Use and ONE Adjust for every screen.
//
// Using stock and correcting a count were each written twice, end to end: once
// on the Use / Adjust screen (InventoryActionCatalog.js) and once on the item
// page (InventoryItemDetail.js). The copies had drifted in five ways (#349's
// amendment lists them), which is CLAUDE.md rule 14's class exactly: the
// duplication was in the write and its side effects, not the look of the sheet.
// materialReceive.js settled the same problem for Receive at Session 64 (#13);
// this is that shape for the other two movements. Both screens -- and a label
// scan straight into Use or Adjust -- now save through these two functions, so
// a rule written here holds everywhere at once (#199 part 1's WO# switch now,
// #363's size threshold at 79.22b.6.6).
//
// What each function settles, once:
//   - Validation, including who may adjust (#374's standard: the write itself
//     refuses, so no route to it can post what the rule forbids).
//   - The stock record exists before the movement is saved (#789). With none,
//     the database's stock rule finds nothing to move: the history would say
//     the count changed and the shelf would show nothing. Receive seeds the
//     record at zero for the same reason (#238); Use and Adjust never did.
//   - The count is read fresh at save, never taken from what a screen loaded.
//   - "On hand after" is written by the database's stock rule from the figure
//     its own update produced (#368, 20261010_running_balance_from_the_stock_rule_368.sql).
//     The value sent here is only a placeholder the rule overwrites.
//   - Where a note goes: a Use note in work_order_ref_note, as Receive and the
//     item page have always written it; an Adjust's reason in notes. The
//     history screens read both (#788).
//   - The managers' alerts, worded as before (#500's recipients, #369's
//     Opening count exception).
//
// REFUSALS ARE RETURNED, NOT THROWN: { refused: '<the sentence to show>' }. A
// database failure still throws, so callers keep their "Something went wrong"
// handling for the unexpected while a refusal is shown as written.

import { supabase } from './supabase';
import { canAdjustInventory } from './permissions';
import { workOrderRefError, normalizeWorkOrderRef } from './workOrderRef';
import { REASON_OPENING_COUNT } from './adjustReasons';
import { sendNotification, getStockAuditRecipientIds } from './notify';

export const USE_QTY_INVALID = 'Please enter a valid quantity.';
// #366's sentence, carried from the item page to both screens (drawing D, D2).
export const USE_NEEDS_REF_OR_NOTE = 'Enter a WO# or a note before submitting.';
// #199 part 1 (drawing D, D3) -- with the company's switch on.
export const USE_NEEDS_WO = 'Enter the WO# this stock is for. Your company requires one.';
export const ADJUST_REFUSED = "Adjusting a count isn't part of your role in Stede. Ask your branch manager to make this change.";
export const ADJUST_COUNT_INVALID = 'Enter the number you physically counted.';
export const ADJUST_NEEDS_REASON = 'Please select a reason.';

// The Use sheet's own check, shared so both screens refuse in the same words
// before anything is sent. submitUseStock() runs it again: the sheet is
// advisory, the write is the rule.
export function stockUseRefusal({ qty, jobRef, notes, requireJobRef }) {
  const n = Number(qty);
  if (!Number.isInteger(n) || n < 1) return USE_QTY_INVALID;
  const ref = normalizeWorkOrderRef(jobRef);
  const woError = workOrderRefError(ref);
  if (woError) return woError;
  if (requireJobRef === true && !ref) return USE_NEEDS_WO;
  if (!ref && !String(notes ?? '').trim()) return USE_NEEDS_REF_OR_NOTE;
  return null;
}

export function adjustStockRefusal({ auth, newQty, reason }) {
  if (!canAdjustInventory(auth || {})) return ADJUST_REFUSED;
  const n = Number(newQty);
  if (newQty === '' || newQty === null || newQty === undefined || !Number.isInteger(n) || n < 0) return ADJUST_COUNT_INVALID;
  if (!reason) return ADJUST_NEEDS_REASON;
  return null;
}

// #789 -- the record at zero, if there is none. A duplicate is ignored, so an
// existing count is never touched.
async function ensureStockRecord(itemId, branchId) {
  const { error } = await supabase
    .from('inventory_stock')
    .upsert(
      { item_id: itemId, branch_id: branchId, qty_on_hand: 0 },
      { onConflict: 'item_id,branch_id', ignoreDuplicates: true }
    );
  if (error) throw error;
}

async function readStock(itemId, branchId) {
  const { data, error } = await supabase
    .from('inventory_stock')
    .select('id, qty_on_hand, avg_unit_cost')
    .eq('item_id', itemId)
    .eq('branch_id', branchId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function submitUseStock({ item, branchId, branchName, qty, jobRef, notes, profile, requireJobRef }) {
  const refused = stockUseRefusal({ qty, jobRef, notes, requireJobRef });
  if (refused) return { refused };
  const used = Number(qty);
  const ref = normalizeWorkOrderRef(jobRef);
  const note = String(notes ?? '').trim();

  await ensureStockRecord(item.id, branchId);
  const stock = await readStock(item.id, branchId);
  const currentQty = Number(stock?.qty_on_hand ?? 0);
  const newQty = currentQty - used;

  const { error: txErr } = await supabase
    .from('inventory_transactions')
    .insert({
      item_id: item.id,
      branch_id: branchId,
      user_id: profile.id,
      tx_type: 'consume',
      qty: -used,
      qty_after: newQty,
      work_order_ref: ref || null,
      work_order_ref_note: note || null,
      unit_cost_at_transaction: stock?.avg_unit_cost ?? null,
    });
  if (txErr) throw txErr;

  const wentNegative = newQty < 0;
  if (wentNegative) {
    // #500 -- the branch's managers plus this person's own department manager.
    const managerIds = await getStockAuditRecipientIds(branchId, profile.department_id);
    await sendNotification(
      managerIds,
      `Audit required · ${branchName}`,
      `${item.name} is now at ${newQty}. ${profile.full_name} used ${used} but only ${currentQty} ${Number(currentQty) === 1 ? 'was' : 'were'} on hand. Count needs verification.`,
      'audit',
      'inventory',
      'inv_detail',
      item.id
    );
  }
  return { currentQty, newQty, wentNegative };
}

// ── #363, Session 79.22b.6.6 -- WHEN A COUNT ADJUSTMENT TELLS THE MANAGERS ────
//
// The approved gate: docs/gates/Stede_AdjustmentThreshold_Gate_363.html. Its
// rule, in the Settings screen's own sentence: "Tell your managers when an
// adjustment is worth more than $250, or moves more than 10% of what the branch
// holds, but never for anything under $50." Written ONCE here (D18): both adjust
// sheets use it to say what will happen before Confirm, and the save uses it to
// decide, from a fresh read of the count, the cost and the settings.
//
// Order matters and is the gate's B2 ladder. Two calls that look like
// shortcuts and are not:
//   - NO COST ON RECORD ALERTS (D9). A missing cost is not a $0 change: every
//     system the gate read would treat it as $0 and post it inside tolerance,
//     and under a $50 floor that silences a 400-sheet loss. A cost of zero is
//     no cost. Silence needs evidence.
//   - SWITCH OFF MEANS TODAY (D13): every adjustment but an opening count
//     alerts, so the feature can never be switched off into silence. Settings
//     that cannot be read count as "couldn't check", which alerts too.

export const ADJUST_ALERT_COLUMNS =
  'adjust_alert_enabled, adjust_alert_floor_dollars, adjust_alert_value_dollars, adjust_alert_share_percent';

// The settings row's four columns as numbers, or null when they cannot be used
// (missing, unreadable, or not numbers) -- which the rule treats as unchecked.
export function adjustAlertSettingsFrom(row) {
  if (!row) return null;
  const s = {
    enabled: row.adjust_alert_enabled === true,
    floor: Number(row.adjust_alert_floor_dollars),
    value: Number(row.adjust_alert_value_dollars),
    share: Number(row.adjust_alert_share_percent),
  };
  if (![s.floor, s.value, s.share].every(Number.isFinite)) return null;
  return s;
}

const cents = n => Math.round((n + Number.EPSILON) * 100) / 100;

// Returns { alert, why, change, value, share }. `why` is one of: opening,
// unchecked, off, no_change, no_cost, under_floor, over_value, over_share,
// within. `value` is dollars rounded to cents BEFORE any comparison (B3), so a
// sentence can never read "$50.00, under the $50 floor"; "under" is strictly
// less and "over" strictly more. `share` is the change against what the branch
// held before it, 100 when it held none or a negative count (D11).
export function adjustAlertDecision({ reason, onHandBefore, counted, unitCost, settings }) {
  const before = Number(onHandBefore ?? 0);
  const change = Number(counted) - before;
  const share = before > 0 ? (Math.abs(change) / before) * 100 : 100;
  const out = (alert, why, value = null) => ({ alert, why, change, value, share: why === 'opening' ? null : share });
  if (reason === REASON_OPENING_COUNT) return out(false, 'opening');
  if (!settings) return out(true, 'unchecked');
  if (!settings.enabled) return out(true, 'off');
  if (change === 0) return out(false, 'no_change');
  const cost = Number(unitCost);
  if (unitCost === null || unitCost === undefined || !Number.isFinite(cost) || cost <= 0) return out(true, 'no_cost');
  const value = cents(Math.abs(change) * cost);
  if (value < settings.floor) return out(false, 'under_floor', value);
  if (value > settings.value) return out(true, 'over_value', value);
  if (share > settings.share) return out(true, 'over_share', value);
  return out(false, 'within', value);
}

// ── The words (gate F2), so the sheet, the done line and the alert agree ──────

export function formatMoney(n) {
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
// A limit reads "$250", or "$250.50" when it carries cents.
export function formatLimitMoney(n) {
  return Number.isInteger(Number(n)) ? `$${Number(n).toLocaleString('en-US')}` : formatMoney(n);
}
// "10", or "12.5" when it carries a fraction.
function formatLimitPercent(n) {
  return String(Number(n));
}
// Signed, with a true minus sign: "−6", "+6".
export function formatChange(n) {
  return n < 0 ? `−${Math.abs(n)}` : `+${n}`;
}
// A whole percent, but one decimal place under 10 (4.7%) and wherever rounding
// would make an over-the-line share read as equal to its limit (10.4% over 10%).
export function formatShare(share, limit) {
  const whole = Math.round(share);
  if (share < 10 || (share > limit && whole === Number(limit))) return (Math.round(share * 10) / 10).toFixed(1);
  return String(whole);
}

export const NOTICE_QUIET = 'Nobody will be notified.';
export const NOTICE_TOLD = 'Your managers will be notified.';

// What the sheet says above Confirm. `canSeeCost` is canSeeInventoryCost(): the
// dollar figure and the limits are shown only to people who may see stock cost
// (D12); everyone else gets the same answer with no number in it.
export function adjustNoticeLines(decision, { canSeeCost, branchName, settings }) {
  const d = decision;
  const figure = d && d.value !== null ? `${formatChange(d.change)} · ${formatMoney(d.value)}` : '';
  switch (d && d.why) {
    case 'opening':
      return { tone: 'quiet', head: NOTICE_QUIET, detail: "An opening count never alerts, whatever its size. It's recorded in the ledger." };
    case 'no_change':
      return { tone: 'quiet', head: NOTICE_QUIET, detail: "The count matches what Stede has. It's recorded as checked." };
    case 'under_floor':
      return { tone: 'quiet', head: NOTICE_QUIET, detail: canSeeCost
        ? `${figure}, under the ${formatLimitMoney(settings.floor)} alert floor. It's still recorded in the ledger.`
        : "It's inside your company's alert limits. It's still recorded in the ledger." };
    case 'within':
      return { tone: 'quiet', head: NOTICE_QUIET, detail: canSeeCost
        ? `${figure}, ${formatShare(d.share, settings.share)}% of what ${branchName} holds, inside your alert limits. It's still recorded in the ledger.`
        : "It's inside your company's alert limits. It's still recorded in the ledger." };
    case 'over_value':
      return { tone: 'told', head: NOTICE_TOLD, detail: canSeeCost
        ? `${figure}, over the ${formatLimitMoney(settings.value)} alert.`
        : "It's over your company's alert limits." };
    case 'over_share':
      return { tone: 'told', head: NOTICE_TOLD, detail: canSeeCost
        ? `${figure}, ${formatShare(d.share, settings.share)}% of what ${branchName} holds, over the ${formatLimitPercent(settings.share)}% alert.`
        : "It's over your company's alert limits." };
    case 'no_cost':
      return { tone: 'told', head: NOTICE_TOLD, detail: `This item has no cost on record at ${branchName}, so Stede can't tell how big the change is.` };
    case 'off':
      return { tone: 'told', head: NOTICE_TOLD, detail: 'Stede tells them about every adjustment except an opening count.' };
    default:
      return { tone: 'told', head: NOTICE_TOLD, detail: "Stede couldn't check this change against your alert limits, so it will tell them." };
  }
}

// What the alert adds to its body when the switch is on (D16). Off: nothing,
// so the alert reads exactly as it always has.
export function adjustAlertClause(decision, { branchName, settings }) {
  const d = decision;
  switch (d && d.why) {
    case 'over_value': return ` Worth ${formatMoney(d.value)}, over the ${formatLimitMoney(settings.value)} alert.`;
    case 'over_share': return ` Worth ${formatMoney(d.value)}, ${formatShare(d.share, settings.share)}% of what ${branchName} held, over the ${formatLimitPercent(settings.share)}% alert.`;
    case 'no_cost': return " No cost on record, so Stede couldn't size it.";
    case 'unchecked': return " Stede couldn't check it against your alert limits.";
    default: return '';
  }
}

// The done screen's line, from what the save DID (D17): "notified" only when an
// alert was actually delivered to somebody.
export const DONE_NOTIFIED = 'Inventory updated and your manager has been notified.';
export const DONE_QUIET = 'Inventory updated and recorded in the ledger. Nobody was notified.';
export const DONE_RECORDED = 'Inventory updated and recorded in the ledger.';
export function adjustDoneLine(result) {
  if (result && result.delivered > 0) return DONE_NOTIFIED;
  const d = result && result.decision;
  if (d && !d.alert && d.why !== 'opening') return DONE_QUIET;
  return DONE_RECORDED;
}

async function readAdjustAlertSettings() {
  try {
    const { data, error } = await supabase
      .from('company_settings')
      .select(ADJUST_ALERT_COLUMNS)
      .limit(1)
      .maybeSingle();
    if (error) return null;
    return adjustAlertSettingsFrom(data);
  } catch (e) {
    return null;
  }
}

// What a sheet needs to say what will happen: the settings and this branch's
// cost, read when the sheet opens. A failed read answers { settings: null },
// which the rule calls "couldn't check" -- the save alerts in that case.
export async function readAdjustAlertInputs(itemId, branchId) {
  const settings = await readAdjustAlertSettings();
  let unitCost = null;
  try {
    const stock = await readStock(itemId, branchId);
    unitCost = stock?.avg_unit_cost ?? null;
  } catch (e) {
    unitCost = null;
  }
  return { settings, unitCost };
}

export async function submitAdjustStock({ auth, item, branchId, branchName, newQty, reason, notes, profile }) {
  const refused = adjustStockRefusal({ auth, newQty, reason });
  if (refused) return { refused };
  const counted = Number(newQty);
  const note = String(notes ?? '').trim();

  await ensureStockRecord(item.id, branchId);
  const stock = await readStock(item.id, branchId);
  const currentQty = Number(stock?.qty_on_hand ?? 0);
  // The signed difference is what is saved; the stock rule adds it (#347), so a
  // movement somebody else made between the read and the save survives.
  const delta = counted - currentQty;

  const { error: txErr } = await supabase
    .from('inventory_transactions')
    .insert({
      item_id: item.id,
      branch_id: branchId,
      user_id: profile.id,
      tx_type: 'adjust',
      qty: delta,
      qty_after: counted,
      notes: `${reason}${note ? ' · ' + note : ''} · Adjusted by ${profile.full_name}`,
      unit_cost_at_transaction: stock?.avg_unit_cost ?? null,
    });
  if (txErr) throw txErr;

  // #369 -- an Opening count loads a balance for the first time; there is
  // nothing to reconcile, so nobody is alerted. #363 -- every other reason is
  // judged by the size rule above, with the settings read now, not when the
  // sheet opened. The record is kept either way.
  const settings = reason === REASON_OPENING_COUNT ? null : await readAdjustAlertSettings();
  const decision = adjustAlertDecision({
    reason, onHandBefore: currentQty, counted, unitCost: stock?.avg_unit_cost ?? null, settings,
  });
  let delivered = 0;
  if (decision.alert) {
    const managerIds = await getStockAuditRecipientIds(branchId, profile.department_id);
    const sent = await sendNotification(
      managerIds,
      `Count adjusted · ${branchName}`,
      `${item.name}: ${currentQty} → ${counted}. Reason: ${reason}. Adjusted by ${profile.full_name}.`
        + adjustAlertClause(decision, { branchName, settings }),
      'audit',
      'inventory',
      'inv_detail',
      item.id
    );
    delivered = Number(sent?.delivered ?? 0);
  }
  return { currentQty, newQty: counted, delta, decision, delivered };
}
